import { getPaylinksMbeUrl } from "../helpers/urls";

export const MAX_PRODUCT_IDS = 10;

/**
 * Fetches the Catalog V2 products (1-10) sold in a product pay link (type "M")
 * @param {string} options.env
 * @param {string} options.storeId
 * @param {string[]} options.productIds
 * @returns {Promise<Product[]>}
 */
export async function getProducts({ env, storeId, productIds }) {
  const ids = [...new Set((productIds || []).filter(Boolean))].slice(0, MAX_PRODUCT_IDS);

  if (!ids.length || !storeId) {
    return [];
  }

  try {
    const response = await fetch(
      `${getPaylinksMbeUrl(env)}/api/v1/catalog/products?storeId=${encodeURIComponent(storeId)}`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ productIds: ids }),
      },
    );

    if (!response.ok) {
      throw new Error(`status ${response.status}`);
    }

    const products = await response.json();

    return Array.isArray(products) ? products.map(normalizeCatalogProduct) : [];
  } catch (error) {
    console.error(`getProducts error: ${error}`);

    return [];
  }
}

const isWordChar = (char) => /[a-z0-9]/i.test(char || "");

// whole-word, case-insensitive match without building a RegExp from data
function labelHasWord(label, word) {
  if (!label || !word) {
    return false;
  }

  const haystack = label.toLowerCase();
  const needle = word.toLowerCase();
  let index = haystack.indexOf(needle);

  while (index !== -1) {
    if (!isWordChar(haystack[index - 1]) && !isWordChar(haystack[index + needle.length])) {
      return true;
    }
    index = haystack.indexOf(needle, index + 1);
  }

  return false;
}

// "Labubu Green" -> "Green" when the product is "Labubu"
function stripPrefix(label, prefix) {
  if (!label || !prefix || !label.toLowerCase().startsWith(`${prefix.toLowerCase()} `)) {
    return label;
  }

  return label.slice(prefix.length).trim() || label;
}

const nodes = (connection) => (connection?.edges || []).map(({ node }) => node).filter(Boolean);

/**
 * Flattens a Catalog V2 sku group (GraphQL edges) into a simpler shape
 * @param {object} product
 */
export function normalizeCatalogProduct(product) {
  const skus = nodes(product?.skus)
    .filter((sku) => !sku.status || sku.status === "ACTIVE")
    .map((sku) => {
      const price = nodes(sku.prices)[0];
      const available = nodes(sku.inventoryCounts).find((count) => count.type === "AVAILABLE");

      return {
        id: sku.id,
        code: sku.code,
        label: sku.label,
        price: price?.value?.value || 0,
        compareAtPrice: price?.compareAtValue?.value,
        currencyCode: price?.value?.currencyCode,
        availableQuantity: available ? available.quantity : null,
      };
    });

  const images = nodes(product?.mediaObjects)
    .filter((media) => !media.type || media.type === "IMAGE")
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map((media) => media.url)
    .filter(Boolean);

  const allAttributes = nodes(product?.attributes).map((attribute) => ({
    id: attribute.id,
    name: attribute.name,
    label: attribute.label || attribute.name,
    values: nodes(attribute.values).map((value) => ({
      id: value.id,
      name: value.name,
      label: value.label || value.name,
    })),
  }));

  // the catalog response doesn't link skus to attribute values, so match each
  // value label as a whole word in the sku label (e.g. "Labubu Green" -> Color: Green)
  skus.forEach((sku) => {
    sku.options = {};

    allAttributes.forEach((attribute) => {
      const match = attribute.values.find((value) => labelHasWord(sku.label, value.label));

      if (match) {
        sku.options[attribute.name] = match.label;
      }
    });

    sku.displayLabel = stripPrefix(sku.label, product?.label);
  });

  // only keep attributes every sku maps to and that actually vary across skus
  const attributes = allAttributes
    .filter((attribute) => skus.length && skus.every((sku) => sku.options[attribute.name]))
    .filter((attribute) => new Set(skus.map((sku) => sku.options[attribute.name])).size > 1)
    .map((attribute) => ({
      ...attribute,
      values: attribute.values.filter((value) =>
        skus.some((sku) => sku.options[attribute.name] === value.label),
      ),
    }));

  // modifiers (e.g. "Gift Message", "Finish"); they don't change the sku
  const modifiers = nodes(product?.options)
    .filter((option) => option.id && ["TEXT", "LIST"].includes(option.type))
    .map((option) => ({
      id: option.id,
      name: option.name,
      label: option.label || option.name,
      type: option.type,
      description: option.description || "",
      min: option.validation?.min ?? 0,
      max: option.validation?.max ?? null,
      price: nodes(option.prices)[0]?.value?.value || 0,
      values: nodes(option.values).map((value) => ({
        id: value.id,
        name: value.name,
        label: value.label || value.name,
        price: nodes(value.prices)[0]?.value?.value || 0,
      })),
    }))
    .filter((modifier) => modifier.type !== "LIST" || modifier.values.length);

  return {
    id: product?.id,
    name: product?.label,
    description: product?.description || "",
    imageUrl: images[0],
    images,
    attributes,
    skus,
    modifiers,
    minPrice: skus.length ? Math.min(...skus.map((sku) => sku.price)) : 0,
  };
}
